import {
  PROTOCOL_VERSION,
  parseProtocolMessage,
  type AnyProtocolMessage,
  type PeerRole,
  type ProtocolMessage,
  type ProtocolPayloads,
  type RoomCredential,
} from '@screenshare/protocol';
import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import { NOOP_LOGGER, writeLog, type StructuredLogger } from './logger';
import type { PeerSignalMessage, PeerSignalType, PeerSignalingChannel } from './types';

const SOCKET_OPEN = 1;
const SOCKET_CLOSING = 2;

export type SignalingState =
  'idle' | 'connecting' | 'authenticating' | 'connected' | 'disconnected' | 'failed' | 'closed';

export interface SignalingEvents {
  readonly statechange: { readonly previous: SignalingState; readonly current: SignalingState };
  readonly message: { readonly message: AnyProtocolMessage };
  readonly error: { readonly error: Error };
}

export interface SignalingClientOptions {
  readonly url: string;
  readonly roomCode: string;
  readonly role: PeerRole;
  readonly credential: RoomCredential;
  readonly logger?: StructuredLogger;
  readonly heartbeatIntervalMs?: number;
  readonly createWebSocket?: (url: string) => WebSocket;
  readonly createMessageId?: () => string;
  readonly resumeTokenStorage?: ResumeTokenStorage | null;
}

export interface ResumeTokenStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SignalingCloseOptions {
  readonly forgetResumeToken?: boolean;
}

export class SignalingClient
  extends TypedEventEmitter<SignalingEvents>
  implements PeerSignalingChannel
{
  private readonly logger: StructuredLogger;
  private readonly createWebSocket: (url: string) => WebSocket;
  private readonly createMessageId: () => string;
  private readonly resumeTokenStorage: ResumeTokenStorage | null;
  private readonly resumeStorageKey: string;
  private readonly peerListeners = new Set<(message: PeerSignalMessage) => void>();
  private socket: WebSocket | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private resumeToken: string | null;
  private epoch = 0;
  private explicitlyClosed = false;
  private _state: SignalingState = 'idle';

  constructor(private readonly options: SignalingClientOptions) {
    super();
    this.logger = options.logger ?? NOOP_LOGGER;
    this.createWebSocket = options.createWebSocket ?? ((url) => new WebSocket(url));
    this.createMessageId = options.createMessageId ?? defaultMessageId;
    this.resumeTokenStorage =
      options.resumeTokenStorage === undefined
        ? browserSessionStorage()
        : options.resumeTokenStorage;
    this.resumeStorageKey = `screenshare.resume.${options.roomCode}.${options.role}`;
    this.resumeToken = this.readResumeToken();
  }

  get state(): SignalingState {
    return this._state;
  }

  get latestResumeToken(): string | null {
    return this.resumeToken;
  }

  connect(): Promise<ProtocolPayloads['room.joined']> {
    if (!['idle', 'disconnected', 'failed'].includes(this._state)) {
      return Promise.reject(new Error(`Cannot connect signaling from ${this._state}.`));
    }

    this.explicitlyClosed = false;
    this.stopHeartbeat();
    const previousSocket = this.socket;
    this.socket = null;
    if (previousSocket !== null && previousSocket.readyState < SOCKET_CLOSING) {
      previousSocket.close(1000, 'Replaced by reconnect');
    }
    this.epoch += 1;
    this.setState('connecting');

    let socket: WebSocket;
    try {
      socket = this.createWebSocket(this.options.url);
    } catch (cause) {
      const error = new Error('Could not create signaling WebSocket.', { cause });
      this.reportError(error);
      return Promise.reject(error);
    }
    this.socket = socket;

    return new Promise((resolve, reject) => {
      let settled = false;
      let failed = false;
      let usedResumeToken = false;

      const failConnect = (error: Error) => {
        if (failed) {
          return;
        }
        failed = true;
        this.stopHeartbeat();
        if (!settled) {
          settled = true;
          reject(error);
        }
        this.reportError(error);
        if (socket.readyState < SOCKET_CLOSING) {
          socket.close(1002, 'Protocol or transport error');
        }
      };

      const handleOpen = () => {
        if (this.socket !== socket) return;
        this.setState('authenticating');
        const credential: RoomCredential = this.resumeToken
          ? { type: 'resume-token', value: this.resumeToken }
          : this.options.credential;
        usedResumeToken = credential.type === 'resume-token';
        this.sendEnvelope('room.join', {
          roomCode: this.options.roomCode,
          role: this.options.role,
          credential,
        });
      };

      const handleMessage = (event: MessageEvent<unknown>) => {
        if (this.socket !== socket) return;
        if (typeof event.data !== 'string') {
          failConnect(new Error('Signaling server sent a non-text protocol frame.'));
          return;
        }
        const parsed = parseProtocolMessage(event.data);
        if (!parsed.ok) {
          failConnect(new Error(`Invalid signaling message: ${parsed.reason}.`));
          return;
        }

        const message = parsed.message;
        if (message.type === 'room.joined') {
          this.resumeToken = message.payload.resumeToken;
          this.writeResumeToken(message.payload.resumeToken);
          this.setState('connected');
          this.startHeartbeat();
          if (!settled) {
            settled = true;
            resolve(message.payload);
          }
        } else if (message.type === 'room.error' || message.type === 'error') {
          if (usedResumeToken && isCredentialRejection(message)) this.clearResumeToken();
          failConnect(new Error(`Signaling server rejected the request: ${message.payload.code}.`));
        }

        if (
          message.type === 'peer.leave' &&
          (message.payload.reason === 'host-ended' || message.payload.reason === 'session-expired')
        ) {
          this.clearResumeToken();
        }

        this.emit('message', { message });
        if (isPeerSignalMessage(message)) {
          for (const listener of this.peerListeners) {
            listener(message);
          }
        }
      };

      const handleError = () => {
        if (this.socket !== socket) return;
        failConnect(new Error('Signaling WebSocket failed.'));
      };

      const handleClose = () => {
        socket.removeEventListener('open', handleOpen);
        socket.removeEventListener('message', handleMessage);
        socket.removeEventListener('error', handleError);
        socket.removeEventListener('close', handleClose);
        const wasActiveSocket = this.socket === socket;
        if (wasActiveSocket) {
          this.stopHeartbeat();
          this.socket = null;
        }
        if (!settled) {
          settled = true;
          reject(new Error('Signaling WebSocket closed before authentication completed.'));
        }
        if (wasActiveSocket && !this.explicitlyClosed && this._state !== 'failed') {
          this.setState('disconnected');
        }
      };

      socket.addEventListener('open', handleOpen);
      socket.addEventListener('message', handleMessage);
      socket.addEventListener('error', handleError);
      socket.addEventListener('close', handleClose);
    });
  }

  send<Type extends PeerSignalType>(type: Type, payload: ProtocolPayloads[Type]): void {
    if (this._state !== 'connected') {
      throw new Error(`Cannot send ${type} while signaling is ${this._state}.`);
    }
    this.sendEnvelope(type, payload);
  }

  setRoomLocked(locked: boolean): void {
    this.sendEnvelope(locked ? 'room.lock' : 'room.unlock', {});
  }

  removeViewer(): void {
    this.sendEnvelope('host.kick', {});
  }

  refreshTurnCredentials(): void {
    this.sendEnvelope('turn.refresh', {});
  }

  subscribe(listener: (message: PeerSignalMessage) => void): () => void {
    this.peerListeners.add(listener);
    return () => this.peerListeners.delete(listener);
  }

  close(options: SignalingCloseOptions = {}): void {
    if (this._state === 'closed') {
      if (options.forgetResumeToken === true) this.clearResumeToken();
      return;
    }
    this.explicitlyClosed = true;
    this.stopHeartbeat();
    const socket = this.socket;
    this.socket = null;
    if (socket !== null && socket.readyState < SOCKET_CLOSING) {
      socket.close(1000, 'Client closed');
    }
    this.setState('closed');
    if (options.forgetResumeToken === true) this.clearResumeToken();
    this.peerListeners.clear();
    this.clearListeners();
  }

  private sendEnvelope<Type extends keyof ProtocolPayloads>(
    type: Type,
    payload: ProtocolPayloads[Type],
  ): void {
    const socket = this.socket;
    if (socket === null || socket.readyState !== SOCKET_OPEN) {
      throw new Error('Signaling WebSocket is not open.');
    }
    const message: ProtocolMessage<Type> = {
      v: PROTOCOL_VERSION,
      type,
      id: this.createMessageId(),
      epoch: this.epoch,
      payload,
    };
    socket.send(JSON.stringify(message));
    writeLog(this.logger, 'debug', 'signaling', 'message.sent', { type, epoch: this.epoch });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    const interval = this.options.heartbeatIntervalMs ?? 15_000;
    if (interval <= 0) {
      return;
    }
    this.heartbeatTimer = setInterval(() => {
      try {
        this.sendEnvelope('heartbeat', { sentAt: Date.now() });
      } catch (cause) {
        this.reportError(new Error('Could not send signaling heartbeat.', { cause }));
      }
    }, interval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private setState(current: SignalingState): void {
    const previous = this._state;
    if (previous === current) {
      return;
    }
    this._state = current;
    writeLog(this.logger, 'info', 'signaling', 'state.changed', { previous, current });
    this.emit('statechange', { previous, current });
  }

  private reportError(error: Error): void {
    writeLog(this.logger, 'error', 'signaling', 'transport.error', {
      name: error.name,
      state: this._state,
    });
    this.emit('error', { error });
    if (this._state !== 'closed' && this._state !== 'disconnected') {
      this.setState('failed');
    }
  }

  private readResumeToken(): string | null {
    try {
      const token = this.resumeTokenStorage?.getItem(this.resumeStorageKey) ?? null;
      return token !== null && /^[A-Za-z0-9_-]{32,128}$/u.test(token) ? token : null;
    } catch {
      return null;
    }
  }

  private writeResumeToken(token: string): void {
    try {
      this.resumeTokenStorage?.setItem(this.resumeStorageKey, token);
    } catch {
      // Storage can be unavailable in privacy modes; in-memory reconnection still works.
    }
  }

  private clearResumeToken(): void {
    this.resumeToken = null;
    try {
      this.resumeTokenStorage?.removeItem(this.resumeStorageKey);
    } catch {
      // A blocked storage API must not interfere with transport teardown.
    }
  }
}

function browserSessionStorage(): ResumeTokenStorage | null {
  try {
    return typeof globalThis.sessionStorage === 'undefined' ? null : globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function isCredentialRejection(
  message: Extract<AnyProtocolMessage, { readonly type: 'room.error' | 'error' }>,
): boolean {
  return (
    (message.type === 'error' &&
      (message.payload.code === 'UNAUTHORIZED' || message.payload.code === 'AUTH_REQUIRED')) ||
    (message.type === 'room.error' &&
      ['ROOM_NOT_FOUND', 'ROOM_EXPIRED', 'VIEWER_REMOVED'].includes(message.payload.code))
  );
}

function isPeerSignalMessage(message: AnyProtocolMessage): message is PeerSignalMessage {
  return ['peer.offer', 'peer.answer', 'peer.ice', 'peer.ready', 'peer.leave'].includes(
    message.type,
  );
}

function defaultMessageId(): string {
  return crypto.randomUUID();
}
